import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
import useCoTermMonths, { MONTH_TO_MONTH } from '../../../hooks/useCoTermMonths'
// UI Components
import Description from '../../ui-helper-elements/Description'
// JS Imports
import { saveOpportunity } from '../../../ts/data'
import { generateBookedQuotePDF } from '../../../js/data.js'
import toast from 'react-hot-toast'

interface BookQuoteButtonProps {
   buttonClass?: string | undefined
   buttonSize?: 'sm' | 'lg' | undefined
}

// Payroll opportunities may only be booked from these SF record types. Any other
// record type is hard-blocked when Payroll has an implementation (not "None").
const PAYROLL_BOOKING_ALLOWED_RECORD_TYPES = [
   '0120z000000gi1LAAQ',
   '0120z000000gi1GAAQ',
   '0124z000000Q9yEAAS',
]

export default function BookQuoteButton(props: BookQuoteButtonProps) {
   // Access the quote context
   const state = useQuoteContext()
   const { getString } = state

   // State for modal visibility and button confirmation
   const [show, setShow] = useState(false)
   const [isConfirmMode, setIsConfirmMode] = useState(false)
   const [isProcessing, setIsProcessing] = useState(false)

   // State for the pre-booking validation popups
   const [showHardBlock, setShowHardBlock] = useState(false)
   const [showPayrollHardBlock, setShowPayrollHardBlock] = useState(false)
   const [showSoftConfirm, setShowSoftConfirm] = useState(false)

   // Use the useFeaturePermissions hook
   const { hasPermission } = useFeaturePermissions()

   // Existing-contract detection (shared with TermSelect / PrepaySelect)
   const { hasActiveContract } = useCoTermMonths()

   // Check permissions using the hook
   const hasDifferenceQuoteReminderPermission = hasPermission(
      'DifferenceQuoteReminder',
      state.user_role,
   )

   // A quote has no HRIS when no core tier (Core/Pro/Elite) is selected.
   const hasNoHRIS =
      !state.coreProductSelected ||
      state.coreProductSelected === 'core0' ||
      state.productCoreEECount <= 0
   const isMonthToMonth = state.termLength === MONTH_TO_MONTH

   // Hard block: a quote without HRIS must be an add-on billed month-to-month
   // or co-termed to an existing contract. Any other term length is not allowed.
   const blockBookingNoHRIS = hasNoHRIS && !isMonthToMonth && !state.isCoTerm

   // Hard block: a Payroll opportunity with an implementation (full/foundations)
   // may only be booked from an approved Payroll record type. "None" implementation
   // is exempt. A non-"none" imp type already implies Payroll is present.
   const hasPayrollImplementation = state.payrollImpType !== 'none'
   const recordTypeAllowsPayroll =
      PAYROLL_BOOKING_ALLOWED_RECORD_TYPES.includes(state.recordType)
   const blockBookingPayrollRecordType =
      hasPayrollImplementation && !recordTypeAllowsPayroll

   // Soft confirm: an existing term contract should usually be co-termed.
   const confirmExistingContract = hasActiveContract && !state.isCoTerm

   // Handle modal open/close
   const handleShow = () => setShow(true)
   const handleClose = () => {
      setShow(false)
      setIsConfirmMode(false) // Reset confirmation state when closing modal
   }

   // Handle button click - only for booking quotes now
   const handleButtonClick = () => {
      handleShow()
   }

   // Handle the primary action of the Book Quote modal. The first click runs
   // the pre-booking validations and surfaces a hard block or a soft confirm
   // as its own popup; when neither applies the existing two-click
   // confirmation is preserved and the second click performs the booking.
   const handleProceed = async () => {
      if (!isConfirmMode) {
         // If both popups would apply, the hard block takes precedence.
         if (blockBookingNoHRIS) {
            handleClose()
            setShowHardBlock(true)
            return
         }
         if (blockBookingPayrollRecordType) {
            handleClose()
            setShowPayrollHardBlock(true)
            return
         }
         if (confirmExistingContract) {
            handleClose()
            setShowSoftConfirm(true)
            return
         }
         // First click - switch to confirmation mode
         setIsConfirmMode(true)
      } else {
         // Second click - proceed with booking the quote
         await executeBooking()
      }
   }

   // Book the quote: save the opportunity, then generate the Order Form PDF.
   // Reused by the two-click confirmation and the existing-contract confirm.
   const executeBooking = async () => {
      if (isProcessing) return // Prevent multiple submissions

      setIsProcessing(true)
      const toastId = toast.loading('Booking Quote...')

      try {
         // Save the opportunity data with booked stage
         toast.loading('Saving opportunity...', { id: toastId })
         const saveResponse = await saveOpportunity({
            state: state,
            stage: 'booked',
            hasUnlimitedDatePermission: hasPermission(
               'UnlimitedActivationDateSelect',
               state.user_role,
            ),
         })

         // If quote saved successfully, update stage state and close fieldset
         if (saveResponse && saveResponse.ok) {
            state.setstage('booked')
            state.setIsClosed(true)

            // Automatically generate PDF for booked quote (Order Form)
            try {
               toast.loading('Generating Order Form PDF...', { id: toastId })
               await generateBookedQuotePDF(state)

               // All operations completed successfully
               toast.success(
                  'Quote booked successfully! The Order Form PDF is now available for the customer in-app to agree to terms and conditions. A copy is also available on this opportunity for viewing.',
                  { id: toastId },
               )
               handleClose()
            } catch (pdfError) {
               // PDF generation failed - need to revert the stage
               console.error(
                  'PDF generation failed, reverting stage:',
                  pdfError,
               )
               toast.loading('Reverting booked status...', { id: toastId })

               // Revert the stage on the frontend (backend may have already done this if request reached server)
               try {
                  const revertResponse = await saveOpportunity({
                     state: state,
                     stage: '', // Empty string defaults to 'Quote' in DB
                     hasUnlimitedDatePermission: true, // Revert must never be blocked
                  })

                  if (revertResponse && revertResponse.ok) {
                     state.setstage('Quote')
                     state.setIsClosed(false)
                     console.log('Successfully reverted stage to Quote')
                  } else {
                     console.error('Failed to revert stage via saveOpportunity')
                     // Still update frontend state even if save failed
                     state.setstage('Quote')
                     state.setIsClosed(false)
                  }
               } catch (revertError) {
                  console.error('Error reverting stage:', revertError)
                  // Still update frontend state even if save failed
                  state.setstage('Quote')
                  state.setIsClosed(false)
               }

               // Close the Book Quote modal
               handleClose()

               // Show error modal with indefinite duration
               toast.error(
                  (t) => (
                     <div
                        className='modal show'
                        style={{ display: 'block', position: 'initial' }}
                     >
                        <Modal.Dialog style={{ maxWidth: '500px' }}>
                           <Modal.Header
                              closeButton
                              onClick={() => toast.dismiss(t.id)}
                           >
                              <Modal.Title>PDF Generation Failed</Modal.Title>
                           </Modal.Header>
                           <Modal.Body>
                              <p>
                                 {pdfError instanceof Error
                                    ? pdfError.message
                                    : 'An error occurred while generating the PDF.'}
                              </p>
                              <p className='mt-2'>
                                 <strong>The quote was not booked.</strong> The
                                 quote status has been automatically reverted.
                                 Please try again or contact support if the
                                 issue persists.
                              </p>
                           </Modal.Body>
                        </Modal.Dialog>
                     </div>
                  ),
                  {
                     id: toastId,
                     style: { maxWidth: '100%', width: 'auto' },
                     duration: Infinity,
                  },
               )

               return // Exit early, don't throw again
            }
         }
         // Handle the case where saveResponse might be undefined
         else if (saveResponse && !saveResponse.ok) {
            const errorText = await saveResponse.text()
            throw new Error(`Failed to save opportunity: ${errorText}`)
         } else {
            throw new Error('Failed to save opportunity: No response received')
         }
      } catch (error) {
         console.error('Error booking quote:', error)

         // Close the Book Quote modal
         handleClose()

         toast.error(
            (t) => (
               <div
                  className='modal show'
                  style={{ display: 'block', position: 'initial' }}
               >
                  <Modal.Dialog>
                     <Modal.Header
                        closeButton
                        onClick={() => toast.dismiss(t.id)}
                     >
                        <Modal.Title>Booking Quote</Modal.Title>
                     </Modal.Header>
                     <Modal.Body>
                        <p>
                           {error instanceof Error
                              ? error.message
                              : 'An error occurred while booking the quote.'}
                        </p>
                     </Modal.Body>
                  </Modal.Dialog>
               </div>
            ),
            {
               id: toastId,
               style: { maxWidth: '60%', width: '500px' },
               duration: Infinity,
            },
         )
      } finally {
         setIsProcessing(false)
      }
   }

   // Existing-contract confirm: book directly, then close the popup.
   const handleSoftConfirmProceed = async () => {
      await executeBooking()
      setShowSoftConfirm(false)
   }

   // Cancel returns the user to the product page without booking.
   const handleSoftConfirmCancel = () => {
      setShowSoftConfirm(false)
   }

   const handleHardBlockClose = () => {
      setShowHardBlock(false)
   }

   const handlePayrollHardBlockClose = () => {
      setShowPayrollHardBlock(false)
   }

   const buttonEnabled = true

   const getDisabledReason = () => {
      return ''
   }

   // Button component - simplified for book-only functionality
   const buttonComponent = (
      <Button
         onClick={handleButtonClick}
         className={props.buttonClass}
         size={props.buttonSize}
         variant='primary'
         style={{
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
         }}
         disabled={!buttonEnabled || isProcessing}
      >
         Book Quote
      </Button>
   )

   return (
      <>
         {!buttonEnabled ? (
            <Description
               content={getDisabledReason()}
               placement='top'
               popoverClassName='warning-popover'
               className='width-text'
               title='Button Disabled'
            >
               {buttonComponent}
            </Description>
         ) : (
            buttonComponent
         )}

         <Modal show={show} onHide={handleClose} centered={true} size='lg'>
            <Modal.Header closeButton>
               <Modal.Title className='w-100 text-center'>
                  Book Quote
               </Modal.Title>
            </Modal.Header>
            <Form noValidate>
               <Modal.Body>
                  <p>
                     Booking this quote means that the client has agreed to the
                     listed products, employee counts, and pricing as presented
                     and is ready to enter payment information. The QTC (NCF)
                     form will be prepopulated with this quote info.{' '}
                     <strong>
                        Quotes cannot be edited after being booked.
                     </strong>
                  </p>
                  {hasDifferenceQuoteReminderPermission &&
                     !state.differenceQuote && (
                        <div className='alert alert-warning mt-3' role='alert'>
                           <strong>Reminder:</strong> The "Difference Quote"
                           option is currently unchecked. Please verify this is
                           intended before proceeding with booking. In almost
                           all cases Expansion reps should use a difference
                           quote.
                        </div>
                     )}
               </Modal.Body>
               <Modal.Footer>
                  <div className='d-flex'>
                     <Button
                        variant='secondary'
                        onClick={handleClose}
                        disabled={isProcessing}
                     >
                        Cancel
                     </Button>
                     <Button
                        variant={isConfirmMode ? 'warning' : 'primary'}
                        onClick={handleProceed}
                        disabled={isProcessing}
                        className='ms-2'
                     >
                        {isProcessing
                           ? getString('global.Processing')
                           : isConfirmMode
                             ? getString('global.AreYouSure')
                             : getString('global.Proceed')}
                     </Button>
                  </div>
               </Modal.Footer>
            </Form>
         </Modal>

         {/* Hard block: no HRIS and not month-to-month / co-term. */}
         <Modal
            show={showHardBlock}
            onHide={handleHardBlockClose}
            backdrop='static'
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString(
                     'BookQuoteButton.HardBlock.Title',
                     'Cannot Book Quote',
                  )}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               {getString(
                  'BookQuoteButton.HardBlock.Body',
                  'Booked quotes without HRIS must be add-ons billed month-to-month, or co-termed to an existing term contract. Please select the correct term length before booking.',
               )}
            </Modal.Body>
            <Modal.Footer>
               <Button variant='primary' onClick={handleHardBlockClose}>
                  {getString('BookQuoteButton.HardBlock.Button', 'OK')}
               </Button>
            </Modal.Footer>
         </Modal>

         {/* Hard block: Payroll implementation on a non-Payroll record type. */}
         <Modal
            show={showPayrollHardBlock}
            onHide={handlePayrollHardBlockClose}
            backdrop='static'
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString(
                     'BookQuoteButton.PayrollHardBlock.Title',
                     'Cannot Book Quote',
                  )}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               {getString(
                  'BookQuoteButton.PayrollHardBlock.Body',
                  'This opportunity includes Payroll with an implementation, but its record type does not support booking Payroll. Book from the correct Payroll record type, or set Payroll Implementation to None before booking.',
               )}
            </Modal.Body>
            <Modal.Footer>
               <Button variant='primary' onClick={handlePayrollHardBlockClose}>
                  {getString('BookQuoteButton.PayrollHardBlock.Button', 'OK')}
               </Button>
            </Modal.Footer>
         </Modal>

         {/* Soft confirm: existing contract that is not being co-termed. */}
         <Modal
            show={showSoftConfirm}
            onHide={handleSoftConfirmCancel}
            backdrop='static'
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString(
                     'BookQuoteButton.SoftConfirm.Title',
                     'Are you sure?',
                  )}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               {getString(
                  'BookQuoteButton.SoftConfirm.Body',
                  'It looks like this customer is already on a term contract. This should probably be booked as a co-term opportunity unless this customer is re-starting a new contract with this opportunity.',
               )}
            </Modal.Body>
            <Modal.Footer>
               <div className='d-flex'>
                  <Button
                     variant='secondary'
                     onClick={handleSoftConfirmCancel}
                     disabled={isProcessing}
                  >
                     {getString('global.Cancel', 'Cancel')}
                  </Button>
                  <Button
                     variant='primary'
                     onClick={handleSoftConfirmProceed}
                     disabled={isProcessing}
                     className='ms-2'
                  >
                     {isProcessing
                        ? getString('global.Processing')
                        : getString('global.Confirm', 'Confirm')}
                  </Button>
               </div>
            </Modal.Footer>
         </Modal>
      </>
   )
}
